import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import admissionApis from "../../api/admissionApis";
import { Admission } from "../../types/admissionType";

interface AdmissionState {
  data: Admission[];
  loading: boolean;
  error: string | null;
  page: number;
  size: number;
  totalPages: number;
  totalElements: number;
  filters: {
    studentName?: string;
    motherName?: string;
    fatherName?: string;
    dateOfBirth?: string;
    academicCycle?: string;
    parentName?:string;
  };
}

const initialState: AdmissionState = {
  data: [],
  loading: false,
  error: null,
  page: 0,
  size: 10,
  totalPages: 0,
  totalElements: 0,
  filters: {},
};

export const fetchAdmissions = createAsyncThunk(
  "admissions/fetchAdmissions",
  async (
    params: {
      page?: number;
      size?: number;
      filters?: AdmissionState["filters"];
      branchCode: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const { page = 0, size = 10, filters, branchCode } = params;
      const response = await admissionApis.getAdmissions({
        page,
        size,
        ...filters,
        branchCode,
      });

      if (response?.status !== 1) {
        return rejectWithValue("Failed to fetch admissions");
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.error || "Something went wrong",
      );
    }
  },
);

const admissionSlice = createSlice({
  name: "admissions",
  initialState,
  reducers: {
    setPage(state, action) {
      state.page = action.payload;
    },
    setSize(state, action) {
      state.size = action.payload;
    },
    setFilters(state, action) {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters(state) {
      state.filters = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdmissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdmissions.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.content;
        state.totalPages = action.payload.totalPages;
        state.totalElements = action.payload.totalElements;
      })
      .addCase(fetchAdmissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setPage, setSize, setFilters, resetFilters } =
  admissionSlice.actions;
export default admissionSlice.reducer;
